import {
	Dialog,
	renderReactDialog,
	Toolbar
} from "client/Components";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { getOpenLocalizationsForDocumentKind, OpenLocalizations } from "client_legacy/Documents/openLocalizations";
import { localize } from "client_legacy/ndaw/culture";
import {
	DocumentKind,
	DocumentItemType,
	DocumentItemViewModel,
	FileTypeFilterKind
} from "client/Api/Documents/SharePoint/common";
import { documentsConfiguration } from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import {
	ReactElement,
	useCallback,
	useMemo,
	useState
} from "react";
import {
	ItemPicker,
	InlineCreationOptions,
	CommonItemPickerOptions,
	ItemPickerOptions,
	SharePointItemPickerOptions,
	WebDavItemPickerOptions,
	Selection,
	ItemPickerLocation,
	ItemPickerMode,
	ItemPickerStoreKind
} from "../ItemPicker/ItemPicker";
import { isNil } from "client/Misc/utils";
import { never } from "client_legacy/ndaw/errors";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import { allowToOpenInLocalApp } from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { openFileInWopi } from "client_legacy/Documents/SharePoint/documentOpener";
import { open as openWebDavInWopi} from "client_legacy/Documents/WebDav/wopiOpener";
import { open as openSharePointInLocalApp } from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import { open as openWebDavInLocalApp } from "client_legacy/Documents/WebDav/localAppOpener";
import { DocumentStoreType, OfficeDocumentEditor } from "client/Api/Documents/common";
import { useEditorPreference } from "../useEditorPreference";
import { getItemsInPreferredOrder, UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { WebDavServerModel } from "client/Api/Documents/WebDav/webDav";

export type OpenDocumentDialogProps = {
	onClose: () => void;
	documentKind: DocumentKind;
};

export const OpenDocumentDialog = (props: OpenDocumentDialogProps): ReactElement => {
	const {
		documentKind,
		onClose
	} = props;

	const [selectedItems, setSelectedItems] = useState<Selection | undefined>(undefined);
	const [location, setLocation] = useState<ItemPickerLocation | undefined>(undefined);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);

	const editorPreference: UserEditorPreference = useEditorPreference();

	const isButtonDisabled: boolean = isNil(selectedItems) || selectedItems.items.length === 0;

	const openLocalizations: OpenLocalizations = getOpenLocalizationsForDocumentKind(documentKind);

	const buttonsVisibility = useMemo(
		(): ButtonsVisibility => {
			if (location?.kind === DocumentStoreType.SharePoint) {
				return {
					isOpenOnlineAvailable: true,
					isOpenLocalAvailable: allowToOpenInLocalApp(),
				};
			}

			const { webDav } = documentsConfiguration();

			if (location?.kind === DocumentStoreType.WebDav && webDav) {
				return {
					isOpenOnlineAvailable: webDav.wopi.isEnabled,
					isOpenLocalAvailable: webDav.localApp.isEnabled,
				};
			}

			return {
				isOpenOnlineAvailable: false,
				isOpenLocalAvailable: false
			};
		},
		[location?.kind]
	);

	const creationOptionsAndTitle = useMemo(
		(): { title: string; creationOptions: InlineCreationOptions; } => {
			const creationOptions: InlineCreationOptions = { folder: true };

			switch (documentKind) {
				case DocumentKind.Word:
					return {
						title: localize("_OpenAWordDocument"),
						creationOptions: { ...creationOptions, word: true }
					};
				case DocumentKind.Excel:
					return {
						title: localize("_OpenAnExcelWorksheet"),
						creationOptions: { ...creationOptions, excel: true }
					};
				case DocumentKind.PowerPoint:
					return {
						title: localize("_OpenAPowerPointPresentation"),
						creationOptions: { ...creationOptions, powerpoint: true }
					};
				case DocumentKind.OneNote:
					return {
						title: localize("_OpenAnOneNoteNotebook"),
						creationOptions: { ...creationOptions, onenote: true }
					};
				case DocumentKind.Visio:
					throw new Error(`DocumentKind ${documentKind} is not supported`);
				default:
					never(documentKind);
			}
		},
		[documentKind]
	);

	const { creationOptions, title } = creationOptionsAndTitle;

	const options: ItemPickerOptions = useMemo(
		() => {
			const supportedStoreKinds: readonly ItemPickerStoreKind[] = getSupportedStoreKinds(documentKind);

			const sharePointOptions: SharePointItemPickerOptions = supportedStoreKinds.includes(DocumentStoreType.SharePoint)
				? {
					isEnabled: true,
					shouldShowPersonalViews: true,
					availableDestination: { kind: "all" }
				}
				: { isEnabled: false };

			const webDavOptions: WebDavItemPickerOptions = supportedStoreKinds.includes(DocumentStoreType.WebDav)
				? {
					isEnabled: true,
					availableDestination: { kind: "all" }
				}
				: { isEnabled: false };

			const commonOptions: CommonItemPickerOptions = {
				mode: ItemPickerMode.SingleFileOrOneNoteNotebook,
				fileTypeFilter: {
					kind: FileTypeFilterKind.OfficeDocument,
					officeDocumentKind: documentKind
				},
				creationOptions
			};

			return {
				sharePointOptions,
				webDavOptions,
				commonOptions
			};
		},
		[creationOptions, documentKind]
	);

	const openOnLocalAppHandler = useCallback(
		(): void => {
			if (
				isNil(selectedItems)
				|| selectedItems.items
					.length === 0
			) {
				throw new Error("no item selected");
			}

			switch (selectedItems.kind) {
				case DocumentStoreType.SharePoint:
					if (location?.kind !== DocumentStoreType.SharePoint) {
						throw new Error(`item selected do not match DocumentStoreType: ${DocumentStoreType.SharePoint}`);
					}

					const file: DocumentItemViewModel = selectedItems.items[0]
						.model;

					if (file.itemType === DocumentItemType.Folder) {
						throw new Error("cannot open folder");
					}

					openSharePointInLocalApp(file);

					showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
						openLocalizations,
						() => openFileInWopi(file)
					);
					break;
				case DocumentStoreType.WebDav:
					if (location?.kind !== DocumentStoreType.WebDav) {
						throw new Error(`item selected do not match DocumentStoreType: ${DocumentStoreType.WebDav}`);
					}

					const server: WebDavServerModel = location.folder
						.server;
					openWebDavInLocalApp(server, selectedItems.items[0]);

					const { webDav } = documentsConfiguration();

					if (!webDav) {
						throw new Error("WebDav is not available");
					}

					if (webDav
							.wopi
							.isEnabled
					) {
						const { id, name } = selectedItems.items[0];
						showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
							openLocalizations,
							() => openWebDavInWopi(server.id, id, name)
						);
					}
					break;
				default:
					never(selectedItems);
			}

			onClose();
		},
		[
			location,
			openLocalizations,
			selectedItems,
			onClose
		]
	);

	const openOnlineAppHandler = useCallback(
		(): void => {
			if (
				isNil(selectedItems)
				|| selectedItems.items
					.length === 0
			) {
				throw new Error("no item selected");
			}

			switch (selectedItems.kind) {
				case DocumentStoreType.SharePoint:
					if (location?.kind !== DocumentStoreType.SharePoint) {
						throw new Error(`item selected do not match DocumentStoreType: ${DocumentStoreType.SharePoint}`);
					}

					const file: DocumentItemViewModel = selectedItems.items[0]
						.model;

					if (file.itemType === DocumentItemType.Folder) {
						throw new Error("cannot open folder");
					}

					openFileInWopi(file);
					break;
				case DocumentStoreType.WebDav:
					if (location?.kind !== DocumentStoreType.WebDav) {
						throw new Error(`item selected do not match DocumentStoreType: ${DocumentStoreType.WebDav}`);
					}

					const { id, name } = selectedItems.items[0];
					const serverId = location.folder
						.server
						.id;
					openWebDavInWopi(
						serverId,
						id,
						name
					);
					break;
				default:
					never(selectedItems);
			}

			onClose();
		},
		[
			location,
			onClose,
			selectedItems
		]
	);

	const selectItemsHandler = useCallback(
		(selectedItems: Selection): void => {
			if (
				isMobile
				&& selectedItems.items
					.length > 0
			) {
				onClose();
				return;
			}
			setSelectedItems(selectedItems);
		},
		[isMobile, onClose]
	);

	const renderOpenOnlineButton = (): ReactElement | null => {
		if (buttonsVisibility.isOpenOnlineAvailable) {
			return (
				<Toolbar.Button
					key="open-online"
					kind="primary"
					onClick={openOnlineAppHandler}
					text={openLocalizations.online.action}
					isDisabled={isButtonDisabled}
					trackingId="open-online"
				/>
			);
		}

		return null;
	};

	const renderOpenInLocalButton = (): ReactElement | null => {
		if (buttonsVisibility.isOpenLocalAvailable) {
			return (
				<Toolbar.Button
					key="open-in-local-app"
					kind="primary"
					onClick={openOnLocalAppHandler}
					text={openLocalizations.local.action}
					isDisabled={isButtonDisabled}
					trackingId="open-in-local-app"
				/>
			);
		}

		return null;
	};

	return (
		<Dialog
			variant="left-slider"
			trackingId="open-document-dialog"
			onClose={onClose}
		>
			<Dialog.Header
				title={title}
				onClose={onClose}
				hasBorder
			/>
			<Dialog.Body goesEdgeToEdge>
				<ItemPicker
					options={options}
					location={location}
					onLocationChange={setLocation}
					selectedItems={selectedItems}
					onItemsSelected={selectItemsHandler}
				/>
			</Dialog.Body>
			{!isMobile && (
				<Dialog.Footer hasBorder>
					<Toolbar>
						{getItemsInPreferredOrder<ReactElement | null>(
							location?.kind === DocumentStoreType.SharePoint
								? documentsConfiguration().sharePoint.defaultOfficeDocumentEditor
								: documentsConfiguration().webDav?.defaultOfficeDocumentEditor ?? OfficeDocumentEditor.Online,
							[
								[
									OfficeDocumentEditor.Online,
									renderOpenOnlineButton
								],
								[
									OfficeDocumentEditor.Local,
									renderOpenInLocalButton
								]
							],
							editorPreference
						)}
						<Toolbar.Button
							kind="secondary"
							onClick={onClose}
							text={localize("_Cancel")}
							trackingId="cancel"
						/>
					</Toolbar>
				</Dialog.Footer>
			)}
		</Dialog>
	);
};

OpenDocumentDialog.displayName="OpenDocumentDialog";

type ButtonsVisibility = {
	isOpenOnlineAvailable: boolean;
	isOpenLocalAvailable: boolean;
};

export function openOpenDocumentDialog(
	props: Omit<OpenDocumentDialogProps, "onClose">
): void {
	renderReactDialog((onClose) => (
		<OpenDocumentDialog {...props} onClose={onClose} />
	));
}

export function isOpenDocumentDialogSupported(documentKind: DocumentKind): boolean {
	return getSupportedStoreKinds(documentKind).length > 0;
}

function getSupportedStoreKinds(documentKind: DocumentKind): readonly ItemPickerStoreKind[] {
	const { sharePoint, webDav } = documentsConfiguration();

	const result: ItemPickerStoreKind[] = [];

	if (sharePoint.isConfigured) {
		result.push(DocumentStoreType.SharePoint);
	}

	if (documentKind !== DocumentKind.OneNote
		&& (webDav && webDav.isConfigured)
	) {
		result.push(DocumentStoreType.WebDav);
	}

	return result;
}